import {
  TIndirectTaxMarketplace,
  TIndirectTaxTransactionStatus as ApiTransactionStatus,
  TIndirectTaxTransactionType as ApiTransactionType,
} from '@commenda/api/indirect-tax/transactions/types';

import { IndirectTaxRegion, IndirectTaxRegionHelper } from '../../common';
import { IndirectTaxTransactionHelper } from '../helpers';
import { TIndirectTaxTransactionTableFilters } from '../types';

interface BuildTransactionApiFiltersArgs {
  corporationId: number;
  region: IndirectTaxRegion;
  // UI filter state (region-scoped), e.g. the filing "View transactions" preset
  // from IndirectTaxFilingsHelper.buildTransactionFiltersForFiling.
  filters: Record<string, string | string[]>;
  searchTerm?: string;
}

// Maps region-scoped UI filter state into the `new-sales-tax/transactions`
// query filters. Extracted from useIndirectTaxTransactionsDashboard so the
// filing detail page can compute the exact same transaction count the
// "View transactions" modal shows (they must always agree).
export function buildIndirectTaxTransactionApiFilters({
  corporationId,
  region,
  filters,
  searchTerm,
}: BuildTransactionApiFiltersArgs): Record<string, unknown> {
  const regionCountries = IndirectTaxRegionHelper.getCountriesForRegion(region);

  const apiFilters: Record<string, unknown> = {
    corporationId,
    search_term: searchTerm || undefined,
  };

  const countryFilter =
    filters[TIndirectTaxTransactionTableFilters.DESTINATION_COUNTRIES];
  const selectedCountries = countryFilter
    ? Array.isArray(countryFilter)
      ? countryFilter
      : [countryFilter]
    : undefined;
  apiFilters.destination_countries = selectedCountries ?? regionCountries;

  const originCountryFilter =
    filters[TIndirectTaxTransactionTableFilters.ORIGIN_COUNTRIES];
  if (originCountryFilter) {
    const origins = Array.isArray(originCountryFilter)
      ? originCountryFilter
      : [originCountryFilter];
    if (origins.length > 0) apiFilters.origin_countries = origins;
  }

  const stateFilter =
    filters[TIndirectTaxTransactionTableFilters.DESTINATION_STATES];
  if (stateFilter) {
    const states = Array.isArray(stateFilter) ? stateFilter : [stateFilter];
    if (states.length > 0) apiFilters.destination_states = states;
  }

  const statusFilter = filters[TIndirectTaxTransactionTableFilters.STATUS];
  if (statusFilter) {
    const statuses = Array.isArray(statusFilter)
      ? statusFilter
      : [statusFilter];
    // Safe: status filter options are typed ApiTransactionStatus enum values
    if (statuses.length > 0)
      apiFilters.statuses = statuses as ApiTransactionStatus[];
  }

  const allowedTransactionTypes =
    IndirectTaxTransactionHelper.getTransactionTypesForRegion(region);
  const typeFilter =
    filters[TIndirectTaxTransactionTableFilters.TRANSACTION_TYPE];
  const selectedTypes = (
    typeFilter ? (Array.isArray(typeFilter) ? typeFilter : [typeFilter]) : []
  ).filter((t) =>
    allowedTransactionTypes.includes(t as ApiTransactionType)
  ) as ApiTransactionType[];
  const effectiveTypes =
    selectedTypes.length > 0 ? selectedTypes : allowedTransactionTypes;
  // Only send when it actually narrows the result (not the full default set).
  if (effectiveTypes.length < Object.keys(ApiTransactionType).length) {
    apiFilters.types = effectiveTypes;
  }

  const sourcePlatformFilter =
    filters[TIndirectTaxTransactionTableFilters.SOURCE_PLATFORM];
  if (sourcePlatformFilter) {
    const platforms = Array.isArray(sourcePlatformFilter)
      ? sourcePlatformFilter
      : [sourcePlatformFilter];
    if (platforms.length > 0) apiFilters.source_platforms = platforms.join(',');
  }

  const marketplaceFilter =
    filters[TIndirectTaxTransactionTableFilters.MARKETPLACE];
  if (marketplaceFilter) {
    const marketplaces = Array.isArray(marketplaceFilter)
      ? marketplaceFilter
      : [marketplaceFilter];
    // Safe: marketplace filter options are typed TIndirectTaxMarketplace values
    if (marketplaces.length > 0)
      apiFilters.marketplaces = marketplaces as TIndirectTaxMarketplace[];
  }

  const periodFilter = filters[TIndirectTaxTransactionTableFilters.PERIOD];
  if (periodFilter) {
    const { fromDate, toDate } =
      IndirectTaxTransactionHelper.parseDateFilterValue(String(periodFilter));
    if (fromDate) apiFilters.from_date = fromDate;
    if (toDate) apiFilters.to_date = toDate;
  }

  const reportingDateFilter =
    filters[TIndirectTaxTransactionTableFilters.REPORTING_DATE];
  if (reportingDateFilter) {
    const { fromDate, toDate } =
      IndirectTaxTransactionHelper.parseDateFilterValue(
        String(reportingDateFilter)
      );
    if (fromDate) apiFilters.reporting_date_override_from_date = fromDate;
    if (toDate) apiFilters.reporting_date_override_to_date = toDate;
  }

  return apiFilters;
}
